import axios from "axios";
import { format } from "date-fns";
import { useEffect, useState } from "react";
import { FaPlusSquare, FaSpinner, FaUser, FaUserCircle } from "react-icons/fa";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import stringToColor from "string-to-color";
import api from "../api";
import { IFeature } from "../api/services/feature.service";
import { IUserInsight, IUserResponse } from "../api/services/user.service";
import { BUILD_ENV } from "../config";
import FeatureDialog from "./FeatureDialog";
import StatusBadge from "./shared/StatusBadge";
import UrlCopyElement from "./shared/UrlCopyElement";

export function getTags(
  onboarded: boolean | undefined,
  verified: boolean | undefined,
  deactivated: boolean | undefined
) {
  return (
    <div className="flex space-x-4">
      {onboarded !== undefined && (
        <StatusBadge
          key={"onboard"}
          status={"ONBOARD"}
          color={onboarded ? "green" : "red"}
        />
      )}
      {verified !== undefined && (
        <StatusBadge
          key={"verified"}
          status={"VERIFIED"}
          color={verified ? "green" : "red"}
        />
      )}
      {deactivated !== undefined && (
        <StatusBadge
          key={"deactivated"}
          status={"DEACTIVATED"}
          color={deactivated ? "green" : "red"}
        />
      )}
    </div>
  );
}

export function printInsights_ABOUT(insight: IUserInsight) {
  return (
    <div className="flex flex-col md:flex-row md:space-x-4 space-y-4 md:space-y-0">
      {insight.insight.occupation && (
        <StatusBadge
          key={"occupation"}
          status={insight.insight.occupation.value}
        />
      )}
      {insight.insight.experience && (
        <StatusBadge
          key={"experience"}
          status={insight.insight.experience.value}
        />
      )}
      {insight.insight.collaborator && (
        <StatusBadge
          key={"collaborator"}
          status={insight.insight.collaborator.value}
        />
      )}
    </div>
  );
}

export function printInsights_AD_PARAMS(insight: IUserInsight) {
  return (
    <div className="flex flex-col md:flex-row md:space-x-4 space-y-4 md:space-y-0">
      {insight.insight.utm_term && (
        <StatusBadge key={"occupation"} status={insight.insight.utm_term} />
      )}
      {insight.insight.hsa_ad && (
        <StatusBadge key={"experience"} status={insight.insight.hsa_ad} />
      )}
      {insight.insight.utm_campaign && (
        <StatusBadge
          key={"collaborator"}
          status={insight.insight.utm_campaign}
        />
      )}
    </div>
  );
}

export function printInsights_BROWSER(insight: IUserInsight) {
  return (
    <div className="flex flex-col md:flex-row md:space-x-4 space-y-4 md:space-y-0">
      {insight.insight.language && (
        <StatusBadge key={"occupation"} status={insight.insight.language} />
      )}
      {insight.insight.platform && (
        <StatusBadge key={"experience"} status={insight.insight.platform} />
      )}
      {/* <StatusBadge key={"collaborator"} status={insight.insight.userAgent} /> */}
    </div>
  );
}

export function getProvider(user: IUserResponse) {
  function getProviderColor(authProviderId: string) {
    switch (authProviderId) {
      case "google":
        return "bg-teal-700 text-white font-bold";
      case "local":
        return "bg-red-700 text-white";
      default:
        return "bg-slate-700 text-slate-100";
    }
  }
  return (
    <StatusBadge
      key={"provider"}
      status={user.authProviderId}
      className={getProviderColor(user.authProviderId)}
    />
  );
}

export function getRole(user: IUserResponse) {
  function getRoleColor(role: string) {
    switch (role) {
      case "USER":
        return "bg-teal-700 text-white font-bold";
      case "ADMIN":
        return "bg-red-700 text-white";
      default:
        return "bg-slate-700 text-slate-100";
    }
  }
  return (
    <StatusBadge
      key={"provider"}
      status={user.role}
      className={getRoleColor(user.role)}
    />
  );
}

export default function UserProfileSection() {
  let { userId } = useParams();

  const [user, setUser] = useState<IUserResponse | null>(null);
  // const [insight, setInsight] = useState<IUserInsight | null>(null);
  const [insights, setInsights] = useState<IUserInsight[]>([]);
  const [features, setFeatures] = useState<IFeature[]>([]);
  const [allFeatures, setAllFeatures] = useState<IFeature[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  const [isModalOpenMember, setIsModalOpenMember] = useState(false);

  useEffect(() => {
    if (userId) {
      setLoading(true);
      fetchUser(userId);
      fetchUserInsights(userId);
      fetchUserFeatures(userId);
      getAllFeatures();
    }
  }, [userId]);

  async function fetchUser(userId: string) {
    try {
      setLoading(true);
      const { data } = await api.user.getUser(userId);
      if (data.result) {
        setUser(data.result);

        setLoading(false);
      }
    } catch (e) {
      if (axios.isAxiosError(e)) {
        if (e.response?.data) {
          toast.error(e.response?.data.errorMessage);
        }
      } else {
        console.log(e);
        toast.error(
          "Something really went wrong, you might want to contact support!"
        );
      }
    }
  }

  async function fetchUserInsights(userId: string) {
    try {
      const { data } = await api.user.getUserInsights(userId);
      if (data.result) {
        setInsights(data.result);
      }
    } catch (e) {
      if (axios.isAxiosError(e)) {
        if (e.response?.data) {
          if (e.response?.status !== 404) {
            toast.error(e.response?.data.errorMessage);
          }
        }
      } else {
        console.log(e);
        toast.error(
          "Something really went wrong, you might want to contact support!"
        );
      }
    }
  }

  async function fetchUserFeatures(userId: string) {
    try {
      const { data } = await api.feature.getUserFeatures(userId);
      if (data.result) {
        setFeatures(data.result);
      }
    } catch (e) {
      if (axios.isAxiosError(e)) {
        if (e.response?.data) {
          if (e.response?.status !== 404) {
            toast.error(e.response?.data.errorMessage);
          }
        }
      } else {
        console.log(e);
        toast.error(
          "Something really went wrong, you might want to contact support!"
        );
      }
    }
  }

  async function getAllFeatures() {
    try {
      setLoading(true);
      const { data } = await api.feature.searchFeatures({
        size: 50,
      });
      if (data.result) {
        const featurePageContent = data.result.content;
        setAllFeatures(featurePageContent);
        setLoading(false);
      }
    } catch (e) {
      if (axios.isAxiosError(e)) {
        if (e.response?.data) {
          toast.error(e.response?.data.errorMessage);
        }
      } else {
        console.log(e);
        toast.error(
          "Something really went wrong, you might want to contact support!"
        );
      }
    }
  }

  async function openUserLogsInNewTab(userId: string, environment: string) {
    const hostname =
      environment === "production"
        ? "mvp-backend-deployment"
        : "mvp-dev-backend-deployment";
    const url = `https://logs.k8.ktr-solutions.com/app/kibana#/discover/?
    _g=(
      filters:!(),
      refreshInterval:(pause:!t,value:0),
      time:(from:now-3d,to:now))&
    _a=(
      columns:!(message),
      filters:!(
      (
        query:(
          match_phrase:(agent.hostname:${hostname})
        )
      ),
      (
        query:(
          match_phrase:('@fields.userId':'${userId}')
        )
      )),
      interval:auto,
      query:(language:kuery,query:''),
      sort:!()
    )`;
    window.open(url.replace(/\s+/g, ""), "_blank");
  }

  function getLogs(user: IUserResponse) {
    return (
      <StatusBadge
        key={"provider"}
        status={"Logs"}
        className={"bg-slate-700 text-slate-100 hover:bg-slate-600"}
        onClick={() => openUserLogsInNewTab(user.id, BUILD_ENV)}
      />
    );
  }

  return (
    <div className="flex flex-col rounded-2xl shadow-xl bg-white grow overflow-hidden">
      <FeatureDialog
        isOpen={isModalOpenMember}
        setIsOpen={setIsModalOpenMember}
        features={allFeatures.filter(
          (feature) => features.indexOf(feature) === -1
        )}
        userId={userId || ""}
      />
      <div className="flex items-center p-4 w-full border-b justify-between">
        <div className="flex items-center">
          <div className="flex items-center mr-10 rounded-lg">
            <div className="">
              <FaUser size={20} className="text-red-700" />
            </div>
            <span className="ml-2 font-medium leading-6 text-slate-700 text-lg flex items-center">
              {!loading && user !== null && (
                <span className="ml-2 text-slate-700">
                  {user.firstname} {user.lastname}
                </span>
              )}
            </span>
          </div>
        </div>
        <div className="flex space-x-2">
          {user && getLogs(user)}
          {user && getRole(user)}
        </div>
      </div>
      {loading && (
        <div className="flex items-center justify-center p-20 text-slate-500">
          <FaSpinner size={30} className="animate-spin" />
        </div>
      )}
      {!loading && user !== null && (
        <div>
          <div className="px-3 md:px-4 py-5 sm:px-6 flex space-x-4 bg-slate-100">
            <FaUserCircle
              className="bg-white rounded-full h-12 w-12"
              color={stringToColor(user.id)}
            />
            <div>
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                User Information
              </h3>
              <p className="mt-1 max-w-2xl text-sm text-gray-500">
                Personal details and usage.
              </p>
            </div>
          </div>
          <div className="">
            <dl>
              <div className="bg-white px-3 md:px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-gray-500">Full name</dt>
                <dd className="mt-1 text-sm font-medium text-gray-900 sm:mt-0 sm:col-span-2">
                  {user.firstname} {user.lastname}
                </dd>
              </div>
              <div className="bg-slate-100 px-3 md:px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-gray-500">
                  Email address
                </dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                  <UrlCopyElement url={user.email} />
                </dd>
              </div>
              <div className="bg-white px-3 md:px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-gray-500">PrimaryId</dt>
                <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                  <UrlCopyElement url={user.id} />
                </dd>
              </div>
              <div className="bg-slate-100 px-3 md:px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-gray-500">
                  Registration
                </dt>
                <dd className="flex justify-between items-center space-x-2 mt-1 text-sm font-semibold text-gray-900 sm:mt-0 sm:col-span-2">
                  <div>
                    {format(new Date(user.createdAt), "dd.MM.yyyy HH:mm:ss")}
                  </div>
                  {user && getProvider(user)}
                </dd>
              </div>

              {insights.map((insight, index) => {
                return (
                  <div className="bg-white px-3 md:px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                    <dt className="text-sm font-medium text-gray-500">
                      Insight - {insight.type}
                    </dt>
                    <dd className="flex flex-col md:flex-row mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                      {insight &&
                        insight.type === "ABOUT" &&
                        printInsights_ABOUT(insight)}
                      {insight &&
                        insight.type === "AD_PARAMS" &&
                        printInsights_AD_PARAMS(insight)}
                      {insight &&
                        insight.type === "BROWSER_PARAMS" &&
                        printInsights_BROWSER(insight)}
                      {/* {insight && getInsights(insight)} */}
                      {!insight && (
                        <div className="flex flex-col md:flex-row md:space-x-4 space-y-4 md:space-y-0 font-semibold bg-slate-300 justify-center rounded px-2.5 py-0.5 text-sm h-6">
                          Insights Not Set
                        </div>
                      )}
                    </dd>
                  </div>
                );
              })}
              {insights.length === 0 && (
                <div className="bg-white px-3 md:px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-500">Insight</dt>
                  <dd className="flex flex-col md:flex-row mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                    <div className="flex flex-col md:flex-row md:space-x-4 space-y-4 md:space-y-0 font-semibold bg-slate-300 justify-center rounded px-2.5 py-0.5 text-sm h-6 ">
                      Insights Not Set
                    </div>
                  </dd>
                </div>
              )}

              <div className="bg-slate-100 px-3 md:px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-gray-500">Tags</dt>
                <dd className="flex flex-col md:flex-row mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                  {getTags(user.onboarded, user.verified, user.deactivated)}
                </dd>
              </div>
              <div className="bg-white px-3 md:px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                <dt className="text-sm font-medium text-gray-500">Features</dt>
                <dd className="flex flex-col md:flex-row md:justify-between mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                  <div className="flex space-x-4">
                    {features.map((feature) => (
                      <StatusBadge key={feature.name} status={feature.name} />
                    ))}
                  </div>
                  <div className="flex space-x-2">
                    <div>Add Feature</div>
                    <div
                      className="text-indigo-600 cursor-pointer tour-1-step1"
                      onClick={() => {
                        setIsModalOpenMember(true);
                      }}
                    >
                      <FaPlusSquare size={24} />
                    </div>
                  </div>
                </dd>
              </div>
            </dl>
          </div>
        </div>
      )}
      {!loading && user === null && (
        <div className="flex flex-col items-center justify-center p-8 text-slate-500">
          <div className="font-semibold text-slate-700 mb-2">
            User Not Found
          </div>
          <div className="inline-flex items-center space-x-2">
            <div>User not found...</div>
          </div>
        </div>
      )}
    </div>
  );
}
